import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProjectSettings,
  updateProjectSettings,
  SettingsConflictError,
} from '../../lib/api/projects';
import type { McpServer, McpTransport, ProjectSettingsView } from '../../../../shared/types/projects';
import { PLANS, PLAN_ORDER, isUnlimited } from '../../../../shared/types/plans';
import { useProjectPlan } from '../../lib/hooks/useProjectPlan';
import type { McpCatalogCategory, McpCatalogEntry } from '../../../../shared/types/mcpCatalog';
import { MCP_CATALOG, CATALOG_CATEGORY_LABELS } from '../../../../shared/types/mcpCatalog';
import { serverNeedsAwsAccount } from '../../../../shared/lib/mcpAws';
import { assembleServerFromCatalog, missingRequiredFields } from '../../../../shared/lib/mcpAssemble';

const TRANSPORT_OPTIONS: McpTransport[] = ['stdio', 'sse', 'http'];

// ---------------------------------------------------------------------------
// Top-level tab component
// ---------------------------------------------------------------------------

export const McpServersTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const settingsQ = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  // Gate on the PROJECT plan via the canonical hook — never the account plan.
  const { plan } = useProjectPlan(projectId);

  if (settingsQ.isLoading)
    return <div className="text-sm text-slate-500">Loading…</div>;
  if (settingsQ.isError)
    return (
      <div role="alert" className="text-sm text-red-600">
        {(settingsQ.error as Error).message}
      </div>
    );
  if (!settingsQ.data) return null;

  return (
    <McpServersEditor
      projectId={projectId}
      initial={settingsQ.data}
      plan={plan}
      onSaved={() => settingsQ.refetch()}
    />
  );
};

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const emptyServer = (): McpServer => ({ name: '', transport: 'stdio', args: [], env: {} });

const McpServersEditor: React.FC<{
  projectId: string;
  initial: ProjectSettingsView;
  plan: import('../../../../shared/types/plans').ProjectPlan;
  onSaved: () => void;
}> = ({ projectId, initial, plan, onSaved }) => {
  const planLocked = !PLANS[plan].features.feature_mcp_servers;
  const mcpCap = PLANS[plan].limits.maxMcpServers;

  const [view, setView] = useState<ProjectSettingsView>(initial);
  const [servers, setServers] = useState<McpServer[]>(initial.settings.mcp_servers ?? []);
  // atCap is only meaningful on plans that CAN have MCP servers but hit the
  // ceiling. Computed after `servers` is declared (was referenced too early).
  const atCap = !planLocked && !isUnlimited(mcpCap) && servers.length >= mcpCap;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);

  const [editingIndex, setEditingIndex] = useState<number | 'new' | null>(null);
  const [editDraft, setEditDraft] = useState<McpServer>(emptyServer());
  // When set, the modal renders the friendly field-based form for this catalog
  // entry instead of the raw command/args editor (FORGE-166).
  const [editCatalog, setEditCatalog] = useState<McpCatalogEntry | null>(null);

  const isDirty = JSON.stringify(servers) !== JSON.stringify(initial.settings.mcp_servers ?? []);

  // Names already in use — passed to both modals for collision detection.
  // When editing an existing entry we exclude its own current name so keeping
  // the same name (no rename) doesn't produce a spurious error.
  const takenNames = useMemo(() => {
    if (typeof editingIndex === 'number') {
      return servers.filter((_, i) => i !== editingIndex).map((s) => s.name);
    }
    return servers.map((s) => s.name);
  }, [servers, editingIndex]);

  const handleSave = async () => {
    setSaveError(null);
    setConflictMsg(null);
    setSaving(true);
    try {
      const newView = await updateProjectSettings(projectId, {
        settings: { ...view.settings, mcp_servers: servers },
        expectedVersion: view.version === 0 ? null : view.version,
      });
      setView(newView);
      setServers(newView.settings.mcp_servers ?? []);
      onSaved();
    } catch (err) {
      if (err instanceof SettingsConflictError) {
        setConflictMsg(err.message);
        setView(err.current);
      } else {
        setSaveError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditCatalog(null);
    setEditDraft(emptyServer());
    setEditingIndex('new');
  };

  const openEdit = (index: number) => {
    setEditCatalog(null);
    setEditDraft({ ...servers[index] });
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    setServers((prev) => prev.filter((_, i) => i !== index));
  };

  /** Open the friendly field-based form for a catalog entry. */
  const handleCatalogAdd = (entry: McpCatalogEntry) => {
    setEditCatalog(entry);
    setEditingIndex('new');
  };

  const closeModal = () => {
    setEditingIndex(null);
    setEditCatalog(null);
  };

  const handleEditSave = useCallback(
    (server: McpServer) => {
      if (editingIndex === 'new') {
        setServers((prev) => [...prev, server]);
      } else if (typeof editingIndex === 'number') {
        setServers((prev) => prev.map((s, i) => (i === editingIndex ? server : s)));
      }
      setEditingIndex(null);
    },
    [editingIndex],
  );

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* Header bar                                                          */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {servers.length === 0
            ? 'No MCP servers configured.'
            : isUnlimited(mcpCap) || planLocked
              ? `${servers.length} server${servers.length !== 1 ? 's' : ''} configured.`
              : `${servers.length} / ${mcpCap} server${mcpCap !== 1 ? 's' : ''} configured.`}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openNew}
            disabled={planLocked || atCap}
            data-testid="add-mcp-server"
            title={atCap ? `${PLANS[plan].label} plan allows at most ${mcpCap} MCP servers` : undefined}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700
                       hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            + Custom server
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || planLocked}
            data-testid="save-mcp-servers"
            className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-md disabled:opacity-40
                       hover:bg-primary-600 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Plan gate banner */}
      {planLocked && (
        <div
          role="alert"
          className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
        >
          <span aria-hidden className="mt-0.5 shrink-0">⚠</span>
          <span>
            MCP servers require the <strong>Team plan or above</strong>. Upgrade this project to add custom MCP servers.
          </span>
        </div>
      )}

      {/* Server cap banner */}
      {atCap && (
        <div
          role="alert"
          data-testid="mcp-cap-banner"
          className="border border-blue-200 bg-blue-50 rounded-lg px-4 py-3 text-sm text-blue-800 flex items-start gap-2"
        >
          <span aria-hidden className="mt-0.5 shrink-0">ℹ</span>
          <span>
            You&apos;ve reached the <strong>{mcpCap}-server limit</strong> for the {PLANS[plan].label} plan.{' '}
            Upgrade to {PLANS[plan].plan === 'team' ? 'Business' : 'Enterprise'} to add more.
          </span>
        </div>
      )}

      {/* Conflict / error banners */}
      {conflictMsg && (
        <div role="alert" className="border border-yellow-300 bg-yellow-50 rounded-md p-2 text-xs text-yellow-900">
          {conflictMsg}
        </div>
      )}
      {saveError && (
        <div role="alert" className="border border-red-200 bg-red-50 text-red-800 text-xs rounded-md p-2">
          {saveError}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Zone 1 — Configured servers                                         */}
      {/* ------------------------------------------------------------------ */}
      {servers.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Configured
          </h3>
          <div className="space-y-2" data-testid="mcp-server-list">
            {servers.map((server, i) => (
              <ServerCard
                key={i}
                server={server}
                onEdit={() => openEdit(i)}
                onRemove={() => handleRemove(i)}
                disabled={planLocked}
              />
            ))}
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Zone 2 — Browse catalog                                             */}
      {/* ------------------------------------------------------------------ */}
      {!planLocked && (
        <section>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Browse catalog
          </h3>
          <CatalogBrowser plan={plan} onAdd={handleCatalogAdd} atCap={atCap} />
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Explainer footer (no external links)                                */}
      {/* ------------------------------------------------------------------ */}
      <p className="text-xs text-slate-400">
        MCP servers are injected at agent launch on Team / Business / Enterprise plans.
        Each server's tools appear prefixed with its <code className="font-mono">name</code>,
        e.g.{' '}
        <code className="font-mono">cloudwatch__execute_log_insights_query</code>.
        Pre-installed servers start instantly; others are fetched on first launch.
      </p>

      {/* Edit / add modal — friendly field form for catalog entries, raw
          command/args editor for custom servers and edits. */}
      {editingIndex !== null && editCatalog && (
        <CatalogServerModal
          entry={editCatalog}
          takenNames={takenNames}
          onSave={(s) => { handleEditSave(s); setEditCatalog(null); }}
          onCancel={closeModal}
        />
      )}
      {editingIndex !== null && !editCatalog && (
        <ServerModal
          draft={editDraft}
          isNew={editingIndex === 'new'}
          takenNames={takenNames}
          onSave={handleEditSave}
          onCancel={closeModal}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Zone 2 — Catalog browser
// ---------------------------------------------------------------------------

const ALL_CATEGORIES = 'all' as const;
type CategoryFilter = typeof ALL_CATEGORIES | McpCatalogCategory;

const CATEGORY_ORDER: McpCatalogCategory[] = [
  'aws', 'observability', 'dev-tools', 'iac', 'security', 'data', 'productivity',
];

const CatalogBrowser: React.FC<{
  plan: import('../../../../shared/types/plans').ProjectPlan;
  onAdd: (entry: McpCatalogEntry) => void;
  atCap?: boolean;
}> = ({ plan, onAdd, atCap = false }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>(ALL_CATEGORIES);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MCP_CATALOG.filter((e) => {
      if (category !== ALL_CATEGORIES && e.category !== category) return false;
      if (q && !e.label.toLowerCase().includes(q) && !e.description.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [search, category]);

  return (
    <div className="space-y-3">
      {/* Search + category tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search servers…"
          data-testid="mcp-catalog-search"
          className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white
                     focus:outline-none focus:ring-1 focus:ring-primary-400 w-48"
        />
        <nav className="flex gap-1 flex-wrap" aria-label="Category filter">
          <CategoryTab
            label="All"
            active={category === ALL_CATEGORIES}
            onClick={() => setCategory(ALL_CATEGORIES)}
            testId="mcp-cat-all"
          />
          {CATEGORY_ORDER.map((c) => (
            <CategoryTab
              key={c}
              label={CATALOG_CATEGORY_LABELS[c]}
              active={category === c}
              onClick={() => setCategory(c)}
              testId={`mcp-cat-${c}`}
            />
          ))}
        </nav>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">No servers match your search.</p>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="mcp-catalog-grid"
        >
          {filtered.map((entry) => (
            <CatalogCard
              key={entry.id}
              entry={entry}
              plan={plan}
              onAdd={onAdd}
              atCap={atCap}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryTab: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  testId?: string;
}> = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={
      'text-xs px-2.5 py-1 rounded-md border transition-colors ' +
      (active
        ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
        : 'border-slate-200 text-slate-600 hover:bg-slate-50')
    }
  >
    {label}
  </button>
);

const CatalogCard: React.FC<{
  entry: McpCatalogEntry;
  plan: import('../../../../shared/types/plans').ProjectPlan;
  onAdd: (entry: McpCatalogEntry) => void;
  atCap?: boolean;
}> = ({ entry, plan, onAdd, atCap = false }) => {
  // Determine if this entry is locked by minPlan.
  const entryMinPlan = entry.minPlan ?? 'team';
  const planRankMap = Object.fromEntries(PLAN_ORDER.map((p, i) => [p, i]));
  const planLocked = (planRankMap[plan] ?? 0) < (planRankMap[entryMinPlan] ?? 0);
  const lockedLabel = planLocked
    ? `${PLANS[entryMinPlan]?.label ?? entryMinPlan}+`
    : null;

  return (
    <article
      data-testid={`mcp-catalog-card-${entry.id}`}
      className={
        'border rounded-xl bg-white p-4 flex flex-col gap-3 ' +
        (planLocked ? 'border-slate-200 opacity-60' : 'border-slate-200 hover:border-primary-200 hover:shadow-sm transition-all')
      }
    >
      {/* Icon + label */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl shrink-0" aria-hidden>{entry.icon}</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{entry.label}</div>
            {entry.source && (
              <div className="text-[10px] text-slate-400">{entry.source}</div>
            )}
          </div>
        </div>
        {/* Chips */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {entry.preinstalled && (
            <span className="text-[10px] font-medium bg-success-50 text-success-700 border border-success-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
              Pre-installed
            </span>
          )}
          {entry.template.requiresAuth && (
            <span className="text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
              Auth required
            </span>
          )}
          {lockedLabel && (
            <span className="text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
              {lockedLabel}
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-slate-600 leading-relaxed flex-1">{entry.description}</p>

      {/* Add button */}
      <button
        type="button"
        disabled={planLocked || atCap}
        onClick={() => onAdd(entry)}
        data-testid={`mcp-catalog-add-${entry.id}`}
        title={atCap ? 'Server limit reached — upgrade to add more' : undefined}
        className="w-full text-xs py-1.5 rounded-md border border-primary-300 text-primary-600
                   hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium"
      >
        Add
      </button>
    </article>
  );
};

// ---------------------------------------------------------------------------
// Server card (read view) — unchanged from original
// ---------------------------------------------------------------------------

const ServerCard: React.FC<{
  server: McpServer;
  onEdit: () => void;
  onRemove: () => void;
  disabled: boolean;
}> = ({ server, onEdit, onRemove, disabled }) => (
  <article className="border border-slate-200 rounded-xl bg-white p-4 flex items-start justify-between gap-4">
    <div className="min-w-0 space-y-1 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-slate-900">{server.name}</span>
        <span className="text-[10px] font-medium uppercase tracking-wide bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">
          {server.transport}
        </span>
      </div>
      <div className="font-mono text-xs text-slate-600 truncate">
        {server.command ?? ''}
        {server.args && server.args.length > 0 && <> {server.args.join(' ')}</>}
      </div>
      {server.url && (
        <div className="text-xs text-slate-500 truncate">
          URL: <span className="font-mono">{server.url}</span>
        </div>
      )}
      {server.env && Object.keys(server.env).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {Object.entries(server.env).map(([k, v]) => (
            <span key={k} className="font-mono text-[10px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">
              {k}={v}
            </span>
          ))}
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={onEdit}
        disabled={disabled}
        aria-label={`Edit ${server.name}`}
        className="text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 disabled:opacity-40 transition-colors"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${server.name}`}
        className="text-xs text-red-600 hover:text-red-700 border border-red-100 rounded px-2 py-1 hover:bg-red-50 disabled:opacity-40 transition-colors"
      >
        Remove
      </button>
    </div>
  </article>
);

// ---------------------------------------------------------------------------
// Add / edit modal — unchanged from original
// ---------------------------------------------------------------------------

type EnvPair = { key: string; value: string };

const ServerModal: React.FC<{
  draft: McpServer;
  isNew: boolean;
  takenNames: string[];
  onSave: (server: McpServer) => void;
  onCancel: () => void;
}> = ({ draft, isNew, takenNames, onSave, onCancel }) => {
  const [name, setName] = useState(draft.name);
  const [transport, setTransport] = useState<McpTransport>(draft.transport);
  const [command, setCommand] = useState(draft.command ?? '');
  const [argsText, setArgsText] = useState((draft.args ?? []).join('\n'));
  const [url, setUrl] = useState(draft.url ?? '');
  const [envPairs, setEnvPairs] = useState<EnvPair[]>(
    Object.entries(draft.env ?? {}).map(([key, value]) => ({ key, value })),
  );
  const [awsRoleArn, setAwsRoleArn] = useState(draft.aws_assume_role_arn ?? '');
  const [awsRegion, setAwsRegion] = useState(draft.aws_region ?? '');
  const [awsExternalId, setAwsExternalId] = useState(draft.aws_external_id ?? '');
  const [touched, setTouched] = useState(false);

  // FORGE-164: AWS-backed servers must target the CLIENT's account — defaulting
  // to Forge's account is never correct. Detect from the live edit state.
  const needsAws = serverNeedsAwsAccount({
    transport: transport as McpTransport,
    command: command.trim() || undefined,
    args: argsText.split('\n').map((s) => s.trim()).filter(Boolean),
    aws_assume_role_arn: awsRoleArn.trim() || undefined,
    aws_region: awsRegion.trim() || undefined,
    aws_external_id: awsExternalId.trim() || undefined,
  });

  const nameError = !name.trim()
    ? 'Name is required.'
    : /\s/.test(name)
    ? 'Name must not contain spaces.'
    : takenNames.includes(name.trim())
    ? `"${name.trim()}" is already in use. To connect two instances, give each a unique name (e.g. jira-eu, jira-us).`
    : null;
  const commandError = transport !== 'sse' && transport !== 'http' && !command.trim() ? 'Command is required for stdio transport.' : null;
  const urlError = (transport === 'sse' || transport === 'http') && !url.trim() ? 'URL is required for SSE / HTTP transport.' : null;
  const awsRoleError = needsAws && !awsRoleArn.trim()
    ? 'Provide the IAM role this server should assume in your AWS account.'
    : null;
  const hasErrors = !!(nameError || commandError || urlError || awsRoleError);

  const addEnvPair = () => setEnvPairs((prev) => [...prev, { key: '', value: '' }]);
  const removeEnvPair = (i: number) => setEnvPairs((prev) => prev.filter((_, j) => j !== i));
  const updateEnvPair = (i: number, field: 'key' | 'value', val: string) =>
    setEnvPairs((prev) => prev.map((p, j) => (j === i ? { ...p, [field]: val } : p)));

  const handleSubmit = () => {
    setTouched(true);
    if (hasErrors) return;
    const trimmedCommand = command.trim();
    const server: McpServer = {
      name: name.trim(),
      transport,
      ...(trimmedCommand ? { command: trimmedCommand } : {}),
      args: argsText.split('\n').map((s) => s.trim()).filter(Boolean),
    };
    if (transport === 'sse' || transport === 'http') server.url = url.trim();
    const envObj = Object.fromEntries(
      envPairs.filter(({ key }) => key.trim()).map(({ key, value }) => [key.trim(), value]),
    );
    if (Object.keys(envObj).length > 0) server.env = envObj;
    // FORGE-164: cross-account AWS — assume a role in the client's account.
    if (awsRoleArn.trim()) {
      server.aws_assume_role_arn = awsRoleArn.trim();
      if (awsRegion.trim()) server.aws_region = awsRegion.trim();
      if (awsExternalId.trim()) server.aws_external_id = awsExternalId.trim();
    }
    onSave(server);
  };

  const fieldCls = (err: string | null) =>
    'block w-full border rounded-md px-2.5 py-1.5 text-sm font-mono bg-white focus:outline-none focus:ring-1 ' +
    (touched && err ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-primary-400');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isNew ? 'Add MCP server' : 'Edit MCP server'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">
            {isNew ? 'Add MCP server' : 'Edit MCP server'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-slate-700">Name <span className="text-red-500">*</span></span>
            <input
              type="text"
              value={name}
              data-testid="mcp-server-name"
              placeholder="cloudwatch"
              onChange={(e) => setName(e.target.value)}
              className={fieldCls(nameError)}
            />
            {touched && nameError && <p className="text-xs text-red-600">{nameError}</p>}
            <p className="text-[10px] text-slate-400">
              Used as the tool prefix in agent tool lists, e.g.{' '}
              <code className="font-mono">{name || 'my-server'}__tool_name</code>.
            </p>
          </label>

          {/* Transport */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-slate-700">Transport <span className="text-red-500">*</span></span>
            <div className="flex gap-2">
              {TRANSPORT_OPTIONS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`transport-${t}`}
                  onClick={() => setTransport(t)}
                  className={
                    'text-xs border rounded-md px-3 py-1.5 ' +
                    (transport === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-50')
                  }
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Command */}
          {(transport === 'stdio' || transport === 'http') && (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-700">
                Command{transport === 'stdio' && <span className="text-red-500"> *</span>}
              </span>
              <input
                type="text"
                value={command}
                data-testid="mcp-server-command"
                placeholder={transport === 'stdio' ? 'uvx' : 'node'}
                onChange={(e) => setCommand(e.target.value)}
                className={fieldCls(commandError)}
              />
              {touched && commandError && <p className="text-xs text-red-600">{commandError}</p>}
            </label>
          )}

          {/* URL */}
          {(transport === 'sse' || transport === 'http') && (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-slate-700">
                URL <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                value={url}
                data-testid="mcp-server-url"
                placeholder="https://mcp.example.com/sse"
                onChange={(e) => setUrl(e.target.value)}
                className={fieldCls(urlError)}
              />
              {touched && urlError && <p className="text-xs text-red-600">{urlError}</p>}
            </label>
          )}

          {/* Args */}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-slate-700">
              Args <span className="text-slate-400">(one per line)</span>
            </span>
            <textarea
              value={argsText}
              data-testid="mcp-server-args"
              placeholder="awslabs.aws-documentation-mcp-server@latest"
              rows={3}
              onChange={(e) => setArgsText(e.target.value)}
              className="block w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm font-mono bg-white focus:outline-none focus:ring-1 focus:ring-primary-400 resize-y"
            />
          </label>

          {/* Env vars */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-700">Environment variables</span>
              <button
                type="button"
                onClick={addEnvPair}
                className="text-xs text-primary-500 hover:text-primary-600 font-medium"
              >
                + Add
              </button>
            </div>
            {envPairs.length === 0 && (
              <p className="text-xs text-slate-400">No environment variables.</p>
            )}
            <div className="space-y-1.5">
              {envPairs.map(({ key, value }, i) => (
                <div key={i} className="flex gap-1.5 items-start">
                  <input
                    type="text"
                    placeholder="KEY"
                    value={key}
                    onChange={(e) => updateEnvPair(i, 'key', e.target.value)}
                    className="w-32 border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                  <span className="text-slate-400 text-xs pt-1">=</span>
                  <input
                    type="text"
                    placeholder="value"
                    value={value}
                    onChange={(e) => updateEnvPair(i, 'value', e.target.value)}
                    className="flex-1 border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                  />
                  <button
                    type="button"
                    onClick={() => removeEnvPair(i)}
                    aria-label="Remove env var"
                    className="text-red-500 hover:text-red-700 text-xs px-1 pt-1"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Cross-account AWS role — required for AWS-backed servers, hidden
              where it's meaningless (non-AWS / docs / remote servers). */}
          {needsAws && (
          <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="mcp-aws-section">
            <span className="text-xs font-medium text-slate-700">
              Your AWS account <span className="text-red-500">*</span>
            </span>
            <p className="text-[10px] text-slate-500">
              This server reads from an AWS account. Provide an IAM role in
              <strong> your AWS account</strong> for the agent to assume — give it read-only access
              to the resources this server needs (e.g. CloudWatch logs and metrics).
              Its trust policy must allow Forge to assume it.
            </p>
            <label className="block space-y-0.5">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">IAM role ARN <span className="text-red-500">*</span></span>
              <input type="text" data-testid="mcp-aws-role-arn" value={awsRoleArn}
                onChange={(e) => setAwsRoleArn(e.target.value)}
                placeholder="arn:aws:iam::111122223333:role/cloudwatch-readonly"
                className={fieldCls(awsRoleError)} />
              {touched && awsRoleError && <p className="text-xs text-red-600">{awsRoleError}</p>}
            </label>
            {awsRoleArn.trim() && (
              <div className="grid grid-cols-2 gap-2">
                <label className="block space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">region</span>
                  <input type="text" data-testid="mcp-aws-region" value={awsRegion}
                    onChange={(e) => setAwsRegion(e.target.value)} placeholder="us-east-1"
                    className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400" />
                </label>
                <label className="block space-y-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">external id (optional)</span>
                  <input type="text" data-testid="mcp-aws-external-id" value={awsExternalId}
                    onChange={(e) => setAwsExternalId(e.target.value)} placeholder="acme-1234"
                    className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400" />
                </label>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="mcp-server-save"
            onClick={handleSubmit}
            className="text-sm px-3 py-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            {isNew ? 'Add server' : 'Update server'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Catalog server modal — friendly, field-based config (FORGE-166)
//
// Renders the labelled inputs a catalog server declares (a DB connection
// string, an API token, an AWS role) with help text, and assembles the
// underlying McpServer behind the scenes. The raw command/args/transport are
// never shown for catalog servers — only the "Add custom server" path exposes
// those (ServerModal above).
// ---------------------------------------------------------------------------

const CatalogServerModal: React.FC<{
  entry: McpCatalogEntry;
  takenNames: string[];
  onSave: (server: McpServer) => void;
  onCancel: () => void;
}> = ({ entry, takenNames, onSave, onCancel }) => {
  const t = entry.template;
  const [name, setName] = useState(t.suggestedName);
  const [values, setValues] = useState<Record<string, string>>({});
  const [awsRoleArn, setAwsRoleArn] = useState('');
  const [awsRegion, setAwsRegion] = useState('');
  const [awsExternalId, setAwsExternalId] = useState('');
  const [touched, setTouched] = useState(false);

  // AWS-backed servers (cloudwatch, bedrock-kb) collect a client role here
  // rather than as a declared field — detected from the fixed template.
  const needsAws = serverNeedsAwsAccount({
    transport: t.transport,
    command: t.command,
    args: t.args,
  });

  const setValue = (key: string, val: string) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const nameError = !name.trim()
    ? 'Name is required.'
    : /\s/.test(name)
    ? 'Name must not contain spaces.'
    : takenNames.includes(name.trim())
    ? `"${name.trim()}" is already in use. To connect two instances, give each a unique name (e.g. jira-eu, jira-us).`
    : null;
  const missing = missingRequiredFields(entry, values);
  const awsRoleError = needsAws && !awsRoleArn.trim()
    ? 'Provide the IAM role this server should assume in your AWS account.'
    : null;
  const hasErrors = !!nameError || missing.length > 0 || !!awsRoleError;

  const handleSubmit = () => {
    setTouched(true);
    if (hasErrors) return;
    onSave(
      assembleServerFromCatalog(entry, name, values, {
        roleArn: awsRoleArn,
        region: awsRegion,
        externalId: awsExternalId,
      }),
    );
  };

  const inputCls = (err: boolean) =>
    'block w-full border rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 ' +
    (touched && err ? 'border-red-400 focus:ring-red-300' : 'border-slate-300 focus:ring-primary-400');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Add ${entry.label}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <span aria-hidden>{entry.icon}</span> Add {entry.label}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-slate-500">{entry.description}</p>

          {/* Name */}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-slate-700">
              Name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              value={name}
              data-testid="mcp-server-name"
              onChange={(e) => setName(e.target.value)}
              className={inputCls(!!nameError) + ' font-mono'}
            />
            {touched && nameError && <p className="text-xs text-red-600">{nameError}</p>}
            <p className="text-[10px] text-slate-400">
              Tool prefix in agent tool lists, e.g.{' '}
              <code className="font-mono">{name || entry.id}__tool_name</code>.
            </p>
          </label>

          {/* Declared fields */}
          {(t.fields ?? []).map((f) => {
            const isMissing = !!(touched && f.required && !(values[f.key] ?? '').trim());
            return (
              <label key={f.key} className="block space-y-1">
                <span className="text-xs font-medium text-slate-700">
                  {f.label}
                  {f.required && <span className="text-red-500"> *</span>}
                </span>
                {f.multiline ? (
                  <textarea
                    value={values[f.key] ?? ''}
                    data-testid={`mcp-field-${f.key}`}
                    placeholder={f.placeholder}
                    rows={6}
                    onChange={(e) => setValue(f.key, e.target.value)}
                    className={inputCls(isMissing) + ' font-mono resize-y'}
                  />
                ) : (
                  <input
                    type={f.secret ? 'password' : 'text'}
                    value={values[f.key] ?? ''}
                    data-testid={`mcp-field-${f.key}`}
                    placeholder={f.placeholder}
                    autoComplete={f.secret ? 'new-password' : 'off'}
                    onChange={(e) => setValue(f.key, e.target.value)}
                    className={inputCls(isMissing) + (f.secret ? ' font-mono' : '')}
                  />
                )}
                {isMissing && <p className="text-xs text-red-600">{f.label} is required.</p>}
                {f.help && <p className="text-[10px] text-slate-400">{f.help}</p>}
              </label>
            );
          })}

          {/* AWS account — for AWS-backed servers (FORGE-164) */}
          {needsAws && (
            <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="mcp-aws-section">
              <span className="text-xs font-medium text-slate-700">
                Your AWS account <span className="text-red-500">*</span>
              </span>
              <p className="text-[10px] text-slate-500">
                This server reads from an AWS account. Provide an IAM role in
                <strong> your AWS account</strong> for the agent to assume — give it read-only
                access to the resources this server needs (e.g. CloudWatch logs and metrics).
                Its trust policy must allow Forge to assume it.
              </p>
              <label className="block space-y-0.5">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  IAM role ARN <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  data-testid="mcp-aws-role-arn"
                  value={awsRoleArn}
                  onChange={(e) => setAwsRoleArn(e.target.value)}
                  placeholder="arn:aws:iam::111122223333:role/cloudwatch-readonly"
                  className={inputCls(!!awsRoleError) + ' font-mono'}
                />
                {touched && awsRoleError && <p className="text-xs text-red-600">{awsRoleError}</p>}
              </label>
              {awsRoleArn.trim() && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block space-y-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">region</span>
                    <input
                      type="text"
                      data-testid="mcp-aws-region"
                      value={awsRegion}
                      onChange={(e) => setAwsRegion(e.target.value)}
                      placeholder="us-east-1"
                      className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </label>
                  <label className="block space-y-0.5">
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">external id (optional)</span>
                    <input
                      type="text"
                      data-testid="mcp-aws-external-id"
                      value={awsExternalId}
                      onChange={(e) => setAwsExternalId(e.target.value)}
                      placeholder="acme-1234"
                      className="block w-full border border-slate-300 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="mcp-server-save"
            onClick={handleSubmit}
            className="text-sm px-3 py-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Add server
          </button>
        </div>
      </div>
    </div>
  );
};

export default McpServersTab;
